/**
 * Energy Chart Component for displaying daily/monthly energy data
 */

import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Chip,
  Box,
  Grid
} from '@mui/material';
import {
  CalendarToday as CalendarDays,
  Bolt as Zap,
  TrendingUp,
  Warning as AlertTriangle
} from '@mui/icons-material';
import { DailyEnergyData } from '../../services/reportingApi';

interface EnergyChartProps {
  data: DailyEnergyData[];
  title: string;
  units: string;
  chartType?: 'line' | 'bar';
  showResetIndicators?: boolean;
  height?: number;
}

export const EnergyChart: React.FC<EnergyChartProps> = ({
  data,
  title,
  units,
  chartType = 'line',
  showResetIndicators = true,
  height = 400
}) => {
  // Format data for charts
  const chartData = useMemo(() => (
    data.map(item => ({
      date: new Date(item.date).toLocaleDateString('en-NZ', {
        month: 'short',
        day: 'numeric'
      }),
      energy: item.energy,
      reset_detected: item.reset_detected,
      data_points: item.data_points,
      fullDate: item.date
    }))
  ), [data]);

  // Calculate statistics
  const { validData, totalEnergy, averageDaily, maxDaily, resetDays } = useMemo(() => {
    const valid = data.filter(item => item.energy !== null);
    const total = valid.reduce((sum, item) => sum + (item.energy || 0), 0);
    const average = valid.length > 0 ? total / valid.length : 0;
    const max = valid.length > 0 ? Math.max(...valid.map(item => item.energy || 0)) : 0;
    const resets = data.filter(item => item.reset_detected).length;
    return {
      validData: valid,
      totalEnergy: total,
      averageDaily: average,
      maxDaily: max,
      resetDays: resets
    };
  }, [data]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box 
          sx={{ 
            bgcolor: 'background.paper', 
            p: 1.5, 
            border: 1, 
            borderColor: 'divider',
            borderRadius: 1, 
            boxShadow: 2 
          }}
        >
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            {label}
          </Typography>
          <Typography variant="body2" color="primary.main" gutterBottom>
            Energy: {data.energy?.toLocaleString() || 'No data'} {units}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Data points: {data.data_points.toLocaleString()}
          </Typography>
          {data.reset_detected && (
            <Box display="flex" alignItems="center" gap={0.5} color="warning.main">
              <AlertTriangle sx={{ fontSize: 12 }} />
              <Typography variant="caption">Reset detected</Typography>
            </Box>
          )}
        </Box>
      );
    }
    return null;
  };

  // Custom dot component for line chart
  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (showResetIndicators && payload.reset_detected) {
      return (
        <circle 
          cx={cx} 
          cy={cy} 
          r={4} 
          fill="#f97316" 
          stroke="#ea580c" 
          strokeWidth={2}
        />
      );
    }
    return <circle cx={cx} cy={cy} r={2} fill="#3b82f6" />;
  };

  return (
    <Card sx={{ width: '100%', mb: 2 }}>
      <CardHeader>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Zap sx={{ color: 'primary.main' }} />
            <Typography variant="h6" component="h2">
              {title}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            {showResetIndicators && resetDays > 0 && (
              <Chip
                icon={<AlertTriangle />}
                label={`${resetDays} resets`}
                variant="outlined"
                size="small"
                sx={{ color: 'warning.main' }}
              />
            )}
            <Chip
              icon={<CalendarDays />}
              label={`${validData.length} days`}
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>
        
        {/* Statistics Row */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={4}>
            <Box textAlign="center" p={2} sx={{ bgcolor: 'primary.light', borderRadius: 1, color: 'white' }}>
              <Typography variant="h4" fontWeight="bold">
                {totalEnergy.toLocaleString()}
              </Typography>
              <Typography variant="body2">Total {units}</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center" p={2} sx={{ bgcolor: 'success.light', borderRadius: 1, color: 'white' }}>
              <Typography variant="h4" fontWeight="bold">
                {averageDaily.toLocaleString(undefined, { maximumFractionDigits: 1 })}
              </Typography>
              <Typography variant="body2">Average Daily {units}</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center" p={2} sx={{ bgcolor: 'secondary.light', borderRadius: 1, color: 'white' }}>
              <Typography variant="h4" fontWeight="bold">
                {maxDaily.toLocaleString()}
              </Typography>
              <Typography variant="body2">Peak Daily {units}</Typography>
            </Box>
          </Grid>
        </Grid>
      </CardHeader>
      
      <CardContent>
        <ResponsiveContainer width="100%" height={height}>
          {chartType === 'line' ? (
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                label={{ value: units, angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="energy"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={<CustomDot />}
                connectNulls={false}
              />
            </LineChart>
          ) : (
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                label={{ value: units, angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="energy" 
                fill="#3b82f6"
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          )}
        </ResponsiveContainer>

        {/* Legend for reset indicators */}
        {showResetIndicators && resetDays > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box display="flex" justifyContent="center" gap={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                <Typography variant="body2">Normal reading</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f97316' }} />
                <Typography variant="body2">Meter reset detected</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
